import React, { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { useBack } from '../../hooks/useBack';
import SwiperCore, { Pagination } from 'swiper';
import { useLanguage } from '../../locale/useLanguage';

import 'swiper/css';
import 'swiper/css/pagination';
import './VerifyDeliveryView.css';
import dayjs from 'dayjs';
import { isToday, isYesterday } from '../../helpers/dateTimes';
import { ComposedMaps, markerIcons } from '../../components/Map/Map';
import { extractLatestServiceAttemptIfExist } from '../../helpers/order';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';

SwiperCore.use([Pagination]);

type VerifyDeliveryViewProps = {};

export const VerifyDeliveryView: React.FC<VerifyDeliveryViewProps> = () => {
  const [t] = useLanguage();
  const goBack = useBack();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const [orderRequestData, setOrderRequestData] = useState<OrderRequestResult>({
    isLoading: true,
    isFetching: false,
  });

  const serviceAttempt = useMemo(() => {
    return extractLatestServiceAttemptIfExist(orderRequestData.data);
  }, [orderRequestData.data]);

  const attemptDate = useMemo(() => {
    if (serviceAttempt) {
      return dayjs(serviceAttempt.timestamp);
    }

    return undefined;
  }, [serviceAttempt]);

  const when = useMemo(() => {
    if (attemptDate) {
      let date: string;
      if (isYesterday(attemptDate)) {
        date = t('verifyPassage.yesterday');
      } else if (isToday(attemptDate)) {
        date = t('verifyPassage.today');
      } else {
        date = attemptDate.format('DD/MM/YY');
      }
      return { when: date, time: attemptDate.format('HH:mm') };
    }

    return undefined;
  }, [attemptDate, t]);

  const isShipperWithHiddenSignature = useMemo(() => {
    return cmsConfigs?.shippersWithHiddenSignature.find(
      (s) => s.shipperId === orderRequestData.data?.shipper.id
    );
  }, [
    cmsConfigs?.shippersWithHiddenSignature,
    orderRequestData.data?.shipper.id,
  ]);

  const isPicExist =
    serviceAttempt && serviceAttempt.photos && serviceAttempt.photos.length > 0;

  return (
    <Layout
      onOrderChange={setOrderRequestData}
      hasBack
      onBackClick={goBack}
      title={t('pageTitles.verifyPassage')}
    >
      <div className="verify-delivery--container">
        {when && serviceAttempt && serviceAttempt.order.lat && (
          <section className="verify-delivery--section">
            <h2 className="subtitle verify-delivery--subtitle">
              {t('verifyPassage.deliveryTime', when)}
            </h2>
            <p>{t('verifyPassage.deliveryTimeDescription')}</p>
            <div className="verify-delivery--map-container">
              <ComposedMaps
                zoom={18}
                centerMap={{
                  lat: serviceAttempt.order.lat,
                  lng: serviceAttempt.order.lon,
                }}
                markers={[
                  {
                    icon: markerIcons.deliveryPoint,
                    lat: serviceAttempt.order.lat,
                    lng: serviceAttempt.order.lon,
                  },
                ]}
              />
            </div>
          </section>
        )}
        {isPicExist && (
          <section className="verify-delivery--section">
            <h2 className="subtitle verify-delivery--subtitle">
              {t('verifyPassage.deliveryPics')}
            </h2>
            <p>{t('verifyPassage.deliveryPicsDescription')}</p>
            <Swiper
              slidesPerView={1}
              centeredSlides
              grabCursor
              className="verify-delivery--images-swiper"
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
            >
              {serviceAttempt.photos!.map((p, i) => {
                return (
                  <SwiperSlide key={i}>
                    <img className="verify-delivery--image" src={p} />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </section>
        )}
        {serviceAttempt?.deliveryTo && (
          <p className="verify-delivery--deliveryTo">
            {t('verifyPassage.noteForCustomer')} {serviceAttempt?.deliveryTo}
          </p>
        )}
        {serviceAttempt &&
          serviceAttempt.signatureURL &&
          !isShipperWithHiddenSignature && (
            <section className="verify-delivery--section">
              <h2 className="subtitle verify-delivery--subtitle">
                {t('verifyPassage.receiverSign')}
              </h2>
              <p>{t('verifyPassage.receiverSignDescription')}</p>
              <div className="verify-delivery--signature-container">
                <img
                  className="verify-delivery--signature"
                  alt="Receiver signature"
                  src={serviceAttempt.signatureURL}
                />
              </div>
            </section>
          )}
      </div>
    </Layout>
  );
};
